/** Annotation issue priority — stored on `RfiRecord.status` / Firestore `status`. */
export type AnnotationPriority = 'neutral' | 'resolved' | 'low' | 'high' | 'critical';

export const ANNOTATION_PRIORITIES: ReadonlyArray<{
    id: AnnotationPriority;
    label: string;
    color: string;
}> = [
    { id: 'neutral', label: 'Neutral', color: '#e8eaed' },
    { id: 'resolved', label: 'Resolved', color: '#50c878' },
    { id: 'low', label: 'Low', color: '#e8d04a' },
    { id: 'high', label: 'High', color: '#ffb43c' },
    { id: 'critical', label: 'Critical', color: '#f04a4a' },
] as const;

/** Gold pin border for Shopify highlight callouts. */
export const COMMERCE_ANNOTATION_HIGHLIGHT_GOLD = '#d4af37';

/** Shopify: binary highlight — stored as `neutral` | `high` in Firestore. */
export const ANNOTATION_COMMERCE_PRIORITIES: ReadonlyArray<{
    id: AnnotationPriority;
    label: string;
    color: string;
}> = [
    { id: 'neutral', label: 'No highlight', color: '#e8eaed' },
    { id: 'high', label: 'Highlight', color: COMMERCE_ANNOTATION_HIGHLIGHT_GOLD },
] as const;

export type CommerceAnnotationHighlight = 'neutral' | 'high';

/** Collapse legacy priority values to binary highlight for Shopify commerce UI + markers. */
export function normalizeCommerceAnnotationHighlight(raw: unknown): CommerceAnnotationHighlight {
    const p = normalizeAnnotationPriority(raw);
    if (p === 'high' || p === 'critical') return 'high';
    return 'neutral';
}

export const ANNOTATION_PRIORITY_FLAG_CLASSES = ANNOTATION_PRIORITIES.map((p) => `flag-${p.id}`);

const priorityById = new Map(ANNOTATION_PRIORITIES.map((p) => [p.id, p]));
const commercePriorityById = new Map(ANNOTATION_COMMERCE_PRIORITIES.map((p) => [p.id, p]));

export function getAnnotationPriorityCatalog(commerceMode = false) {
    return commerceMode ? ANNOTATION_COMMERCE_PRIORITIES : ANNOTATION_PRIORITIES;
}

export function getAnnotationPriorityFieldLabel(commerceMode = false) {
    return commerceMode ? 'Highlight' : 'Priority';
}

/** Map legacy `open` / `issue` and unknown values to the current priority set. */
export function normalizeAnnotationPriority(raw: unknown): AnnotationPriority {
    if (raw === 'resolved') return 'resolved';
    if (raw === 'low') return 'low';
    if (raw === 'high' || raw === 'issue') return 'high';
    if (raw === 'critical') return 'critical';
    if (raw === 'neutral' || raw === 'open') return 'neutral';
    return 'neutral';
}

export function priorityToFlagClass(priority: AnnotationPriority): string {
    return `flag-${priority}`;
}

function renderSelectTrigger(
    trigger: HTMLButtonElement,
    priority: AnnotationPriority,
    commerceMode = false
) {
    const catalog = commerceMode ? commercePriorityById : priorityById;
    const level = catalog.get(priority) ?? catalog.get('neutral')!;
    trigger.replaceChildren();
    const swatch = document.createElement('span');
    swatch.className = 'annotation-priority-select__swatch';
    swatch.style.setProperty('--priority-color', level.color);
    const label = document.createElement('span');
    label.className = 'annotation-priority-select__label';
    label.textContent = level.label;
    const chevron = document.createElement('span');
    chevron.className = 'annotation-priority-select__chevron';
    chevron.setAttribute('aria-hidden', 'true');
    chevron.textContent = '\u25BE';
    trigger.appendChild(swatch);
    trigger.appendChild(label);
    trigger.appendChild(chevron);
    const fieldLabel = getAnnotationPriorityFieldLabel(commerceMode);
    trigger.setAttribute('aria-label', `${fieldLabel}: ${level.label}`);
}

/** Dark-theme square priority dropdown (single swatch + label, not a row of dots). */
export function buildAnnotationPrioritySelect(
    initial: AnnotationPriority,
    onChange: (priority: AnnotationPriority) => void,
    commerceMode = false
): HTMLDivElement {
    const wrap = document.createElement('div');
    wrap.className = 'annotation-priority-select';

    let active: AnnotationPriority = commerceMode
        ? normalizeCommerceAnnotationHighlight(initial)
        : normalizeAnnotationPriority(initial);

    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'annotation-priority-select__trigger';
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-expanded', 'false');

    const menu = document.createElement('div');
    menu.className = 'annotation-priority-select__menu';
    menu.setAttribute('role', 'listbox');
    menu.hidden = true;

    let docListener: ((e: MouseEvent) => void) | null = null;

    const syncOptionActive = () => {
        menu.querySelectorAll('.annotation-priority-select__option').forEach((el) => {
            el.classList.toggle('is-active', (el as HTMLElement).dataset.priority === active);
        });
    };

    const disarmOutsideClose = () => {
        if (docListener) {
            document.removeEventListener('click', docListener, true);
            docListener = null;
        }
    };

    const closeMenu = () => {
        menu.hidden = true;
        trigger.setAttribute('aria-expanded', 'false');
        wrap.classList.remove('is-open');
        disarmOutsideClose();
    };

    const openMenu = () => {
        menu.hidden = false;
        trigger.setAttribute('aria-expanded', 'true');
        wrap.classList.add('is-open');
        syncOptionActive();
        if (docListener) return;
        docListener = (e: MouseEvent) => {
            if (!wrap.contains(e.target as Node)) closeMenu();
        };
        document.addEventListener('click', docListener, true);
    };

    for (const level of getAnnotationPriorityCatalog(commerceMode)) {
        const opt = document.createElement('button');
        opt.type = 'button';
        opt.className = 'annotation-priority-select__option';
        opt.dataset.priority = level.id;
        opt.setAttribute('role', 'option');
        const swatch = document.createElement('span');
        swatch.className = 'annotation-priority-select__swatch';
        swatch.style.setProperty('--priority-color', level.color);
        const label = document.createElement('span');
        label.className = 'annotation-priority-select__option-label';
        label.textContent = level.label;
        opt.appendChild(swatch);
        opt.appendChild(label);
        opt.addEventListener('click', (e) => {
            e.stopPropagation();
            closeMenu();
            if (level.id === active) return;
            active = level.id;
            renderSelectTrigger(trigger, active, commerceMode);
            syncOptionActive();
            onChange(level.id);
        });
        menu.appendChild(opt);
    }

    trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        if (menu.hidden) openMenu();
        else closeMenu();
    });

    renderSelectTrigger(trigger, active, commerceMode);
    syncOptionActive();

    wrap.appendChild(trigger);
    wrap.appendChild(menu);
    return wrap;
}

/** Read-only priority row for viewer-mode hover cards — label + coloured square. */
export function buildAnnotationPriorityReadonly(
    priority: AnnotationPriority,
    commerceMode = false
): HTMLDivElement | null {
    const normalized = commerceMode
        ? normalizeCommerceAnnotationHighlight(priority)
        : normalizeAnnotationPriority(priority);
    if (commerceMode && normalized === 'neutral') {
        return null;
    }
    const catalog = commerceMode ? commercePriorityById : priorityById;
    const level = catalog.get(normalized) ?? catalog.get('neutral')!;
    const fieldLabel = getAnnotationPriorityFieldLabel(commerceMode);
    const row = document.createElement('div');
    row.className = 'annotation-hover-priority-row annotation-hover-priority-row--readonly';

    const label = document.createElement('span');
    label.className = 'annotation-hover-priority-label';
    label.textContent = `${fieldLabel}:`;

    const swatch = document.createElement('span');
    swatch.className = 'annotation-priority-readonly__swatch';
    swatch.style.setProperty('--priority-color', level.color);
    swatch.setAttribute('role', 'img');
    swatch.setAttribute('aria-label', `${fieldLabel}: ${level.label}`);

    row.appendChild(label);
    row.appendChild(swatch);
    return row;
}
